import { useRef, useState } from "react";
import { Sparkles, Languages, Wand2, Expand, StopCircle, Undo2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { streamAssist } from "@/lib/aiStudio";

const ACTIONS = [
  { id: "polish-en", label: "Polish EN", icon: Wand2, mode: "polish", lang: "en", into: "en" },
  { id: "polish-ar", label: "تحرير AR", icon: Wand2, mode: "polish", lang: "ar", into: "ar" },
  { id: "to-ar", label: "EN → AR", icon: Languages, mode: "translate", lang: "en", into: "ar" },
  { id: "to-en", label: "AR → EN", icon: Languages, mode: "translate", lang: "ar", into: "en" },
  { id: "expand", label: "Expand EN", icon: Expand, mode: "expand", lang: "en", into: "en" },
];

const SEO_ACTION = { id: "seo", label: "Write meta", icon: Sparkles, mode: "seo", lang: "en", into: "en" };

/**
 * The AI row that sits under a bilingual field. Streams the result straight
 * into the English or Arabic side and keeps one level of undo.
 */
export default function AiToolbar({ fieldLabel, value, onChange, context, testKey, allowExpand = true, allowSeo = false }) {
  const [busy, setBusy] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [sync, setSync] = useState(true);
  const abortRef = useRef(null);

  const v = value || { en: "", ar: "" };
  const actions = [
    ...(allowSeo ? [SEO_ACTION] : []),
    ...(allowExpand ? ACTIONS : ACTIONS.filter((a) => a.id !== "expand")),
  ];

  const run = async (action) => {
    const own = (v[action.lang] || "").trim();
    const source = action.mode === "seo" ? own || (context || "").trim() : own;
    if (!source) {
      toast.error(
        action.mode === "seo"
          ? "Add a page title or some copy first so the assistant has something to summarise."
          : `Write something in the ${action.lang === "en" ? "English" : "Arabic"} box first.`,
      );
      return;
    }
    setPrevious(v);
    setBusy(action.id);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const english = await streamAssist(
        {
          mode: action.mode,
          text: source,
          lang: action.lang,
          target_lang: action.into,
          field: fieldLabel,
          context: context || "",
        },
        (text) => onChange({ ...v, [action.into]: text }),
        controller.signal,
      );

      // keep the Arabic faithful to the English the editor just changed
      const mirrors = sync && action.into === "en" && action.mode !== "translate";
      if (mirrors && english.trim()) {
        setBusy("syncing");
        await streamAssist(
          {
            mode: "translate",
            text: english,
            lang: "en",
            target_lang: "ar",
            field: fieldLabel,
            context: context || "",
          },
          (text) => onChange({ ...v, en: english, ar: text }),
          controller.signal,
        );
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setBusy(null);
      abortRef.current = null;
    }
  };

  const stop = () => abortRef.current?.abort();

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`ai-toolbar-${testKey}`}>
      <span className="flex items-center gap-1 pe-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary/70">
        <Sparkles className="h-3 w-3" /> AI
      </span>
      {actions.map((a) => (
        <button
          key={a.id}
          type="button"
          disabled={!!busy}
          onClick={() => run(a)}
          data-testid={`ai-${testKey}-${a.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary disabled:opacity-40"
        >
          <a.icon className="h-3 w-3" />
          {busy === a.id ? "Writing…" : a.label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setSync((s) => !s)}
        disabled={!!busy}
        title="When on, rewriting the English immediately regenerates the Arabic to match"
        data-testid={`ai-${testKey}-sync`}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
          sync ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground"
        }`}
      >
        <RefreshCw className={`h-3 w-3 ${busy === "syncing" ? "animate-spin" : ""}`} />
        {busy === "syncing" ? "Syncing Arabic…" : sync ? "Arabic auto-synced" : "Arabic manual"}
      </button>
      {busy && (
        <button
          type="button"
          onClick={stop}
          data-testid={`ai-${testKey}-stop`}
          className="inline-flex items-center gap-1 rounded-full border border-destructive/40 px-2.5 py-1 text-[11px] text-destructive"
        >
          <StopCircle className="h-3 w-3" /> Stop
        </button>
      )}
      {!busy && previous && (
        <button
          type="button"
          onClick={() => {
            onChange(previous);
            setPrevious(null);
          }}
          data-testid={`ai-${testKey}-undo`}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <Undo2 className="h-3 w-3" /> Undo AI
        </button>
      )}
    </div>
  );
}
